import _ from 'lodash';

import { bindServices } from '@/services/api';
import { toPaginate, getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';

const api = bindServices('reportTokens');
const { BulkActionType } = CoreEnums;
const namespace = 'reportTokens';
const fetchRoute = 'reportToken';
const fetchParams = 'ReportToken';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams]?.paged?.select || '',
  searchSelect: CoreQueries[fetchParams]?.paged?.searchSelect || [],
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
  openTokenTraceDrawer: false,
  tokenTraceList: [],
  paginationTokenTrace: { ...initialPagination },
  orderByReportToken: '',
  tokenTraceSearchTable: '',
  openTokenTraceDetail: false,
  idTrace: null,
};

const Model = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...CoreQueries[fetchParams]?.paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: { ...res, limit: payload.limit ?? pageSize, page: payload.page ?? 1 },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = payload.data || payload;
        const cleanData = {
          ...data,
          processId: data.processId?.value ?? data.processId ?? null,
          processInstanceId: data.processInstanceId?.value ?? data.processInstanceId ?? null,
          dynamicEntityId: data.dynamicEntityId?.value ?? data.dynamicEntityId ?? null,
          type: data.type?.value ?? data.type ?? null,
          reportType: data.reportType?.value ?? data.reportType ?? null,
        };
        yield call(api.create, { route: fetchRoute, data: cleanData });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'showForm',
          payload: { state: false, id: null, item: null, detail: false },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = payload.data || payload;
        const cleanData = {
          ...data,
          processId: data.processId?.value ?? data.processId ?? null,
          processInstanceId: data.processInstanceId?.value ?? data.processInstanceId ?? null,
          dynamicEntityId: data.dynamicEntityId?.value ?? data.dynamicEntityId ?? null,
          type: data.type?.value ?? data.type ?? null,
          reportType: data.reportType?.value ?? data.reportType ?? null,
        };
        yield call(api.update, { route: fetchRoute, data: cleanData });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null },
        });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: {
              state: false,
              item: null,
              detail: false,
              id: null,
            },
          });
        }
        yield call(api.remove, { route: fetchRoute, id });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchActivate({ payload }, { put, call }) {
      try {
        yield call(api.activate, {
          route: fetchRoute,
          id: payload.id,
          isActive: payload.state,
        });
        yield put({ type: 'tokenActivate', payload });
      } catch (e) {
        /*  */
      }
    },

    *showForm({ payload }, { put }) {
      if (!_.isNil(payload.id) && payload.item) {
        yield put({ type: 'editOrDetail', payload });
      } else {
        yield put({
          type: 'queryShowForm',
          payload: {
            state: payload.state,
            id: payload.id,
            item: null,
            detail: payload.detail,
          },
        });
      }
    },

    *toggleTokenTraceDrawer({ payload }, { put }) {
      yield put({
        type: 'queryToggleTokenTraceDrawer',
        payload,
      });
    },

    *fetchTokenTraceList({ payload }, { call, put }) {
      try {
        const filterStr = payload.filter
          ? getFiltersGlobalFilters({ tokenTraces: payload.filter }, 'tokenTraces')
          : null;
        const params = {
          route: `reportToken/traces/${payload.idReportToken}`,
          page: payload.page,
          limit: payload.limit,
          search: payload.search,
          orderBy: payload.orderBy,
          ...(filterStr && { filter: filterStr }),
          ...CoreQueries.ManageTokenTrace.paged,
        };
        const response = yield call(api.getAll, params);
        yield put({
          type: 'queryTokenTraceList',
          payload: {
            ...response,
            page: payload.page,
            limit: payload.limit,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *toggleTokenTraceDetail({ payload }, { put }) {
      yield put({
        type: 'queryToggleTokenTraceDetail',
        payload,
      });
    },

    *fetchItemTrace({ payload }, { call, put }) {
      try {
        const params = {
          route: `reportToken/traces/${payload.idReportToken}`,
          ...CoreQueries.ManageTokenTrace.detail,
          filter: `id eq ${payload.idTrace}`,
        };
        const response = yield call(api.getAll, params);
        yield put({
          type: 'queryFetchItemTrace',
          payload: response,
        });
      } catch (e) {
        /*  */
      }
    },

    *bulkDeleteReportTokens({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const state = yield select((s) => s[namespace]);
        const globalState = yield select((s) => s.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(state.orderBy && { orderBy: state.orderBy }),
          ...(filters && { filter: filters }),
          select: state.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *setOrderTokenTrace({ payload }, { put }) {
      yield put({
        type: 'querySetOrderTokenTrace',
        payload,
      });
    },

    *hideAllForms(_, { put }) {
      yield put({ type: 'hideForms' });
    },

    *resetState(_, { put }) {
      yield put({ type: 'resetStateReducer' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items || [],
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
      };
    },

    success(state, { payload }) {
      const isObjectPayload = _.isPlainObject(payload);
      return {
        ...state,
        ...(isObjectPayload ? payload : {}),
        success: isObjectPayload && _.has(payload, 'success') ? payload.success : payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload?.validationErrors || [],
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        id: payload.id,
        item: payload.item,
        detail: payload.detail,
        success: false,
        validationErrors: [],
      };
    },

    editOrDetail(state, { payload }) {
      return {
        ...state,
        showForm: true,
        id: payload.id,
        item: payload.item,
        detail: payload.detail,
      };
    },

    setOrderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    tokenActivate(state, { payload }) {
      const list = (state.list || []).map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },

    hideForms(state) {
      return {
        ...state,
        showForm: false,
        item: null,
        detail: false,
        id: null,
      };
    },

    changeSuccess(state, { payload }) {
      return {
        ...state,
        success: payload.success,
      };
    },

    resetStateReducer() {
      return initialState;
    },

    queryToggleTokenTraceDrawer(state, { payload }) {
      const base = { ...state, openTokenTraceDrawer: payload.state, id: payload.id };
      if (!payload.state) {
        return {
          ...base,
          tokenTraceList: [],
          paginationTokenTrace: { ...initialPagination },
          orderByReportToken: '',
          tokenTraceSearchTable: '',
          openTokenTraceDetail: false,
          idTrace: null,
        };
      }
      return { ...base, orderByReportToken: 'created desc', tokenTraceSearchTable: '' };
    },

    queryTokenTraceList(state, { payload }) {
      return {
        ...state,
        tokenTraceList: payload.items || [],
        paginationTokenTrace: _.merge(state.paginationTokenTrace, toPaginate(payload)),
      };
    },

    setTokenTraceSearchTable(state, { payload }) {
      return {
        ...state,
        tokenTraceSearchTable: payload ?? '',
      };
    },

    clearTokenTraceList(state) {
      return {
        ...state,
        tokenTraceList: [],
        paginationTokenTrace: { ...initialPagination },
      };
    },

    querySetOrderTokenTrace(state, { payload }) {
      return {
        ...state,
        orderByReportToken: payload.value,
      };
    },

    queryToggleTokenTraceDetail(state, { payload }) {
      return {
        ...state,
        openTokenTraceDetail: payload.state,
        idTrace: payload.state ? payload.idTrace : null,
        selectedItemTrace: payload.state && payload.item ? payload.item : null,
      };
    },

    queryFetchItemTrace(state, { payload }) {
      return {
        ...state,
        selectedItemTrace: payload.items?.[0],
      };
    },
  },
};

export default Model;
